import React, { useEffect, useState } from 'react';
import { Table, Loader, Message, Segment, Header, Button, Icon, Input, Modal, Dropdown } from 'semantic-ui-react';
import config from '../../../common/repository/config';
// import DailyStatusModal from './DailyStatusModal';
import LocalStorage from '../../../lib/LocalStorage';
import { toast } from '../../common/Toast';
import { useSelector } from 'react-redux';
import { AppState } from '../../../redux';

interface DailyStatus {
    service_type: string;
    service_status: string;
    service_feedback: string;
    service_closure_date: string;
    service_start_date: string;
}

interface DailyStatusItem {
    case_id: string;
    claim_number: string;
    case_state: string;
    insurance_company_name: string;
    tpa_name: string;
    zonal_head_name: string;
    zonal_head_number: string;
    status_state: string;
    daily_status?: DailyStatus[];
    allocation_status: { investigator_name: string }[];

}

interface ApiResponse {
    data: {
        total_item_count: number;
        items: DailyStatusItem[];
    };
    message?: string | null;
}

const DailyStatusList: React.FC = () => {
    const authState = useSelector((state: AppState) => state.auth);

    const user = authState.user;

    const [items, setItems] = useState<DailyStatusItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dailyStatusDetailModalOpen, setDailyStatusDetailModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<DailyStatusItem | null>(null);
    const [closureDateFeedbackInput, setClosureDateFeedbackInput] = useState('');
    const [dailyInputs, setDailyInputs] = useState<{ [key: string]: { date?: string; feedback?: string } }>({});
    const [closureDateInput, setClosureDateInput] = useState('');
    const [dailyStatusModalOpen, setDailyStatusModalOpen] = useState(false);
    const [dailyStatusNotifyList, setDailyStatusNotifyList] = useState<any[]>([]);
    const [downloading, setDownloading] = useState(false);
    const [tat, setTat] = useState('');
    const [fromDate, setFromDate] = useState('');

    const tatOptions = [
        { key: '2', text: '2 Days', value: '2' },
        { key: '4', text: '4 Days', value: '4' },
        { key: '5', text: '5 Days', value: '5' },
    ];


    useEffect(() => {
        const fetchDailyStatusList = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
                if (!token) {
                    setError('Authentication token is missing.');
                    return;
                }

                const response = await fetch(`${config.apiBaseURL}/notification/listDailyStatus`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-access-token': token,
                    },
                    body: JSON.stringify({}),
                });

                const result: ApiResponse = await response.json();

                if (!response.ok || result.data === null) {
                    throw new Error(result.message || 'Failed to fetch daily status list');
                }

                setItems(result.data.items || []);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
                console.error(err);
                setError(errorMessage);
            }
            finally {
                setLoading(false);
            }
        };


        fetchDailyStatusList();
    }, []);

    const handleOpenStatus = (item: DailyStatusItem) => {
        setSelectedItem(item);
        setDailyStatusDetailModalOpen(true);
    };

    const handleDateChange = (id: string, date: string) => {
        setDailyInputs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                date,
            },
        }));
    };

    const isTodayOrPast = (inputDate: string): boolean => {
        const today = new Date();
        const selected = new Date(inputDate);
        selected.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return selected <= today;
    };

    const handleFeedbackChange = (id: string, feedback: string) => {
        setDailyInputs(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                feedback,
            },
        }));
    };

    const handleSubmitFeedback = async (notify: any) => {
        const caseId = notify.case_id;
        if (!closureDateInput) {
            toast.error("Please select overall closure date.");
            return;
        }
        if (!closureDateFeedbackInput) {
            toast.error("Please enter overall closure feedback.");
            return;
        }
        const dailyStatusItems = notify.daily_status.map((status: any) => {
            const input = dailyInputs[status._id] || {};
            return {
                _id: status._id,
                serviceClosureDate: input.date || '',
                serviceFeedback: input.feedback || '',
            };
        });


        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) {
                toast.error("Authentication token is missing.");
                return;
            }

            const allocatorId = user?.getId();

            const response = await fetch(`${config.apiBaseURL}/case/updateDailyStatus`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify({
                    caseId,
                    allocatorId,
                    closureDate: closureDateInput,
                    closureDateFeedback: closureDateFeedbackInput,
                    dailyStatus: dailyStatusItems,
                }),
            });

            const responseData = await response.json();
            if (responseData.success === true) {
                const successMessage =
                    responseData.message === "No updates applied"
                        ? "Feedback updated successfully"
                        : responseData.message || "Feedback updated successfully";
                toast.success(successMessage);
                setSelectedItem(null);

                setDailyInputs({});
                setClosureDateInput('');
                setClosureDateFeedbackInput('');
                setSelectedItem(null);
            } else {
                toast.error(responseData.message || "Failed to update feedback");
            }



            return responseData;
        } catch (error) {
            console.error('Error updating feedback:', error);
            toast.error("Something went wrong. Please try again.");
            return false;
        }
    };

    const handleClose = () => {
        setSelectedItem(null)
    };

    const handleBulkDownload = async (allocationId: string, fromDate: string, tat: string) => {
        setDownloading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) {
                console.error('Authentication token is missing.');
                return;
            }
            const allocatorId = user?.getId();
            const response = await fetch(`${config.apiBaseURL}/case/downloadDailyStatus`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify({
                    userId: allocatorId,
                    from: fromDate,
                    tat
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to download: ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const disposition = response.headers.get('Content-Disposition');
            let fileName = `Evidence_${allocationId}.zip`;
            if (disposition && disposition.includes('attachment')) {
                const parts = disposition.split(';');
                for (let part of parts) {
                    part = part.trim();
                    if (part.startsWith('filename=')) {
                        fileName = part.substring(9).replace(/['"]/g, '');
                    }
                }
            }

            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log('ZIP file downloaded successfully');
        } catch (error) {
            console.error('Error in handleBulkDownload:', error);
        } finally {
            setDownloading(false);
        }
    };


    return (
        <Segment>
            <Header as="h3">📋 Daily Status List</Header>






            {loading && <Loader active inline="centered" content="Loading..." />}
            {error && <Message negative>{error}</Message>}

            {!loading && !error && items.length === 0 && (
                <Message info>No daily status data found.</Message>
            )}

            {!loading && !error && items.length > 0 && (
                <Table celled striped>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Claim Number</Table.HeaderCell>
                            <Table.HeaderCell>Case State</Table.HeaderCell>
                            <Table.HeaderCell>Insurance Company</Table.HeaderCell>
                            <Table.HeaderCell>TPA</Table.HeaderCell>
                            <Table.HeaderCell>Zonal Head</Table.HeaderCell>

                            <Table.HeaderCell>Service Type</Table.HeaderCell>
                            <Table.HeaderCell>Service Status</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {items.map((item, idx) => (
                            <Table.Row key={idx}>
                                <Table.Cell>{item.claim_number}</Table.Cell>
                                <Table.Cell>{item.case_state}</Table.Cell>
                                <Table.Cell>{item.insurance_company_name}</Table.Cell>
                                <Table.Cell>{item.tpa_name || '-'}</Table.Cell>
                                <Table.Cell>{item.zonal_head_name}</Table.Cell>
                                <Table.Cell>{item.daily_status?.[0]?.service_type || '-'}</Table.Cell>
                                <Table.Cell>{item.daily_status?.[0]?.service_status || '-'}</Table.Cell>
                                <Table.Cell textAlign="center">
                                    <Button
                                        icon
                                        size="small"
                                        style={{ backgroundColor: '#DEFF8B', color: '#000', width: "110px" }}
                                        onClick={() => handleOpenStatus(item)}
                                    >
                                        Open
                                    </Button>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
            {selectedItem && (
                <Modal
                    // key={selectedItem._id || index}
                    open={true}
                    onClose={handleClose}
                    size="large"
                    closeIcon
                >
                    <Modal.Header>📋 {selectedItem.claim_number} - Daily Status</Modal.Header>

                    <Modal.Content
                        scrolling
                        style={{
                            background: '#f9f9f9',
                            borderRadius: '10px',
                            padding: '20px',
                        }}
                    >
                           <h4 style={{ color: '#d02121' }}>
                            Zonal Head: {selectedItem.zonal_head_name}
                        </h4>

                    <h4 style={{ color: '#2185d0' }}>
                            Investigator: {selectedItem.allocation_status[0]?.investigator_name}
                        </h4>    
                     
                        <p><strong>Case State:</strong> {selectedItem.case_state}</p>
                        <p><strong>Insurance Company:</strong> {selectedItem.insurance_company_name}</p>
                        <p><strong>TPA:</strong> {selectedItem.tpa_name}</p>
                        <p><strong>Status:</strong> {selectedItem.status_state}</p>

                        <h5 style={{ marginTop: '20px' }}>Daily Status Updates</h5>

                        <div className="ui relaxed divided list" style={{ marginTop: '10px' }}>
                            {selectedItem.daily_status?.map((status: any, idx: number) => {
                                const input = dailyInputs[status._id] || {};
                                const isEnabled = input.date && isTodayOrPast(input.date);

                                return (
                                    <div
                                        key={status._id}
                                        className="item"
                                        style={{
                                            background: '#ffffff',
                                            borderRadius: '8px',
                                            padding: '20px 25px',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                            marginBottom: '20px',
                                        }}
                                    >
                                        <div className="content">
                                            <div
                                                className="header"
                                                style={{
                                                    fontSize: '1.4em',
                                                    color: '#2185d0',
                                                    marginBottom: '10px',
                                                }}
                                            >
                                                Service #{idx + 1}
                                            </div>

                                            <div className="description" style={{ fontSize: '1.1em', lineHeight: '1.6' }}>
                                                <p><strong>Service Type:</strong> {status.service_type}</p>
                                                <p>
                                                    <strong>Status:</strong>{' '}
                                                    <span
                                                        style={{
                                                            color: status.service_status === 'Finished' ? '#008000' : '#ff0000',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {status.service_status}
                                                    </span>
                                                </p>
                                                <p><strong>Feedback:</strong> {status.service_feedback || '—'}</p>
                                                <p><strong>Start Date:</strong> {new Date(status.service_start_date).toLocaleString()}</p>
                                                <p><strong>Closure Date:</strong> {status.service_closure_date ? new Date(status.service_closure_date).toLocaleString() : '—'}</p>

                                                <div style={{ marginTop: '10px' }}>
                                                    <label><strong>Select Closure Date:</strong></label>
                                                    <Input
                                                        type="date"
                                                        value={input.date || ''}
                                                        onChange={(e) => handleDateChange(status._id, e.target.value)}
                                                        style={{ marginTop: '5px' }}
                                                        disabled={status.service_status === "Finished"}
                                                    />
                                                </div>

                                                {isEnabled && (
                                                    <div style={{ marginTop: '15px' }}>
                                                        <label><strong>Feedback:</strong></label>
                                                        <Input
                                                            placeholder="Enter feedback"
                                                            value={input.feedback || ''}
                                                            onChange={(e) => handleFeedbackChange(status._id, e.target.value)}
                                                            fluid
                                                            style={{ marginTop: '5px' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>


                        <div style={{ marginTop: '30px', background: '#fff', padding: '15px', borderRadius: '8px' }}>
                            <h4>Case Closure</h4>

                            <div style={{ marginBottom: '10px' }}>
                                <label><strong> Closure Date:</strong></label>
                                <Input
                                    type="date"
                                    value={closureDateInput}
                                    onChange={(e) => setClosureDateInput(e.target.value)}
                                    style={{ marginTop: '5px' }}
                                />
                            </div>

                            <div>
                                <label><strong> Closure Feedback:</strong></label>
                                <Input
                                    placeholder="Enter case-level feedback"
                                    value={closureDateFeedbackInput}
                                    onChange={(e) => setClosureDateFeedbackInput(e.target.value)}
                                    fluid
                                    style={{ marginTop: '5px' }}
                                />
                            </div>
                        </div>

                        <Button
                            color="blue"
                            floated="right"
                            onClick={() => handleSubmitFeedback(selectedItem)}
                            style={{ marginTop: '30px' }}
                        >
                            Submit All Feedback
                        </Button>
                    </Modal.Content>
                </Modal>
            )}
        </Segment>
    );
};

export default DailyStatusList;
